import { useCallback, useRef, useState, type ReactNode } from 'react';
import { entrar } from '../lib/auth.js';

/**
 * Pede a senha de ADMINISTRADOR na hora de uma acao destrutiva (apagar movel,
 * apagar/ajustar planta). Funcionario ve e mede; so admin apaga.
 *
 * Uso: const [modal, pedirAdmin] = useSenhaAdmin();  ...  if (await pedirAdmin()) tenta de novo
 */
export function useSenhaAdmin(): [ReactNode, () => Promise<boolean>] {
  const [aberto, setAberto] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const pedir = useCallback(() => new Promise<boolean>((res) => {
    resolver.current = res;
    setAberto(true);
  }), []);

  const fechar = (ok: boolean) => {
    setAberto(false);
    resolver.current?.(ok);
    resolver.current = null;
  };

  return [aberto ? <ModalSenhaAdmin onOk={() => fechar(true)} onCancelar={() => fechar(false)} /> : null, pedir];
}

function ModalSenhaAdmin({ onOk, onCancelar }: { onOk: () => void; onCancelar: () => void }) {
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);

  async function confirmar(e: React.FormEvent) {
    e.preventDefault();
    if (!senha || enviando) return;
    setEnviando(true);
    const r = await entrar(senha);
    setEnviando(false);
    if (r.ok && r.nivel === 'admin') return onOk();
    if (r.ok) return setErro('Essa é a senha de funcionário. Esta ação pede a senha de administrador.');
    setErro(r.motivo === 'bloqueado' ? 'Muitas tentativas. Aguarde 15 minutos.' : 'Senha incorreta.');
  }

  return (
    <div className="gate gate-modal" role="dialog" aria-modal="true" aria-label="Senha de administrador">
      <form className="gate-card" onSubmit={confirmar}>
        <b style={{ textAlign: 'center' }}>Senha de administrador</b>
        <div className="muted small" style={{ textAlign: 'center' }}>Apagar e ajustar exigem permissão de administrador.</div>
        <input type="password" autoFocus placeholder="Senha de administrador" value={senha}
          autoComplete="off" onChange={(e) => { setSenha(e.target.value); setErro(''); }} />
        {erro && <div className="error-box small">{erro}</div>}
        <div className="row">
          <button type="button" onClick={onCancelar}>Cancelar</button>
          <button type="submit" className="primary" disabled={enviando}>{enviando ? 'Conferindo…' : 'Confirmar'}</button>
        </div>
      </form>
    </div>
  );
}
